import {
  useDebugValue,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  createContext,
  useContext,
  ReactNode,
} from 'react';
import { cloneDeep, defaultsDeep, omit, pick } from 'lodash';
import TemplateProcessor from 'stated-js';
import { format as uikFormat, TimeRangeSelector, toast } from '@appd/uik';
import {
  applyCustomBgColor,
  buildObserveUrl,
  buildDashboardUrl,
  buildUrlWithQuery,
  getCircularReplacer,
  getExamplesInfo,
  isValidObject,
  parseDateTime,
  getDisplayDate,
  toFormat,
  format,
  getAbsoluteTimeRange,
  toISO8601StringForCEST,
  metricFormatString,
  toDurationInMinutes,
  toTimeRangeDisplayLabel,
  toTimeRangeDisplayLocal,
} from '../../../../utils';
import {
  getHealthStatusCount,
  getStateCount,
  statusToHealthColor,
  statusToHealthLabel,
  statusToHealthClassName,
  statusToHealthIcon,
} from '../../../health';
import { toGaugeStatus, toGaugeTooltip, toGaugeTitle, statusToHealthColor as toGaugeFillColor } from '../../../gauge';
import { handleOrphanDataPoints, shouldHandleOrphans, transformArrayPoints } from '../../cartesian';
import { toHealthScatterSeries } from '../../Scatter';
import { toHeatMapCategories } from '../../HeatMap';
import {
  toHeatMapSeriesWithIdAsYAxis,
  toHeatMapSeriesTiles,
  getTopInstanceIdsByMetricValue,
  toHeatMapSeries,
} from '../../HeatMap';
import { logger, toValueIndexes } from '../../../../utils';
import { DashboardJson } from '../../../models';
import { PartialQueryResults } from '../../../../models/DatasourcesModels';
import { deepMerge } from '../../../../utils/deepMerge';
import {
  getGlobals,
  setGlobals,
  emits,
  addTemplateProcessor,
  removeTemplateProcessor,
  mergeTemplateGlobals,
} from '../globals';
import { emitShellEvent } from '../utils/emitShellEvent';
import customMethodsForExpressions from '../../widget/visualizationTypes/customMethodsForExpressions';
import { downloadFileFromFetchRsp } from '../../../../utils/downloadFileFromFetchRsp';

const toNumberShort = uikFormat.number.short;
const formatDateTime = uikFormat.dateTime;
const getTimeRange = (timerange: TimeRangeSelector.TimeRange) => TimeRangeSelector.parse(timerange);

const CUSTOM_FUNCTIONS_IN_JSONATA_EXPRESSIONS = {
  defaultsDeep,
  deepMerge,
  pick,
  omit,
  toFormat,
  toNumberShort,
  toValueIndexes,
  formatDateTime,
  toISO8601StringForCEST,
  toTimeRangeDisplayLocal,
  parseDateTime,
  getDisplayDate,
  statusToHealthIcon,
  statusToHealthColor,
  statusToHealthClassName,
  statusToHealthLabel,
  getHealthStatusCount,
  getStateCount,
  toHealthScatterSeries,
  toHeatMapSeries,
  toHeatMapCategories,
  toHeatMapSeriesWithIdAsYAxis,
  toHeatMapSeriesTiles,
  toGaugeStatus,
  toGaugeTooltip,
  toGaugeTitle,
  toGaugeFillColor,
  getTimeRange,
  getAbsoluteTimeRange,
  handleOrphanDataPoints,
  shouldHandleOrphans,
  transformArrayPoints,
  getExamplesInfo,
  getTopInstanceIdsByMetricValue,
  buildDashboardUrl,
  buildObserveUrl,
  buildUrlWithQuery,
  applyCustomBgColor,
  format,
  metricFormatString,
  toDurationInMinutes,
  toTimeRangeDisplayLabel,
  toast,
  downloadFileFromFetchRsp,
  ...customMethodsForExpressions,
};

/** ***********************************************************************************************************
 *  useTemplateStore hook
 ** ***********************************************************************************************************/

export const TemplateContext = createContext<TemplateProviderStore | undefined>(undefined);

interface TemplateProviderStore {
  subscribe: (listener: () => void) => () => void;
  initialize: () => Promise<void>;
  get: () => unknown;
  set: (path: string, data: unknown) => Promise<void>;
  setStatesFromQueryResults: (states: PartialQueryResults) => void;
}

export const useTemplateStore = (
  selector: (s: unknown) => unknown,
): [unknown, (path: string, data: unknown) => Promise<void>, (states: PartialQueryResults) => void] => {
  // use library?
  const store = useContext<TemplateProviderStore | undefined>(TemplateContext);
  if (store === undefined) {
    // throw new Error('useStore must be used within a StoreProvider');
    return [{}, async () => {}, () => {}];
  }
  const state = useSyncExternalStore(
    (store as TemplateProviderStore)?.subscribe,
    () => selector(store.get()),
    () => store.get(),
  );

  useDebugValue(state);

  return [state, store.set, store.setStatesFromQueryResults];
};

/** ***********************************************************************************************************
 *  createTemplateStore
 ** ***********************************************************************************************************/

interface TemplateProcessorOutput {
  name?: string;
  datasources?: Record<string, unknown>;
  datamutations?: Record<string, unknown>;
  view?: unknown[];
  props?: unknown;
  globals?: Record<string, unknown>;
  children?: unknown;
}
interface TemplateProcessorState {
  name?: string;
  datasources?: object;
  datamutations?: object;
  queryResultStatuses?: PartialQueryResults;
  localTemplates?: ReadonlyArray<DashboardJson>;
  importedTemplates?: ReadonlyArray<DashboardJson>;
  view?: unknown;
  props?: unknown;
  globals?: Record<string, unknown>;
  children?: unknown;
  output?: TemplateProcessorOutput;
}

const createTemplateStore = (
  id: string,
  json: DashboardJson,
  props = {},
  emits: (event: string, data: unknown) => Promise<void>,
) => {
  let isInitialized = false;
  const listeners = new Set<() => void>();
  const spec = isValidObject(json.spec) ? cloneDeep(json.spec) : {};
  const localTemplates = json.templates as DashboardJson[];
  const importedTemplates = json.imports as DashboardJson[];
  const globals = getGlobals();
  const globalsJson = cloneDeep(json.globals ?? {});
  const runtimeGlobals = deepMerge({}, { globals }, { globals: globalsJson });
  const name = json.name;
  const runtimeSpecWithGlobals = deepMerge({}, { id, name, props, ...runtimeGlobals }, spec);

  const templateProcessor = new TemplateProcessor(runtimeSpecWithGlobals, {
    ...CUSTOM_FUNCTIONS_IN_JSONATA_EXPRESSIONS,
    emits,
    navigateTo: async (path: string): Promise<void> => {
      emits('navigateTo', path);
    },
    setGlobals,
    stringify: (v: unknown, s: number = 0) => JSON.stringify(v, getCircularReplacer(), s),
    emitShellEvent,
  });

  // templateProcessor.logger.level = 'debug'; // uncomment for debugging

  const templateProcessorOutput: TemplateProcessorOutput = templateProcessor.output;

  let state: TemplateProcessorState = {
    name: json.name,
    localTemplates,
    importedTemplates,
    ...templateProcessorOutput,
  };
  templateProcessor.setDataChangeCallback('/', (data: unknown, ptr: string) => {
    logger.log('TemplateProcessor.setDataChangeCallback', { data, ptr });
    state = { ...state, ...templateProcessorOutput };
    if (Array.isArray(ptr) && ptr.some((p: string) => p.startsWith('/datasources'))) {
      state.datasources = { ...templateProcessorOutput.datasources };
    }
    if (Array.isArray(ptr) && ptr.some((p: string) => p.startsWith('/datamutations'))) {
      state.datamutations = { ...templateProcessorOutput.datamutations };
    }
    if (Array.isArray(ptr) && ptr.some((p: string) => p.startsWith('/view'))) {
      state.view = [...(templateProcessorOutput.view as unknown[])];
    }
    listeners.forEach(listener => listener());
  });

  return {
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    initialize: async () => {
      if (isInitialized) return;
      await templateProcessor.initialize();
      isInitialized = true;
      addTemplateProcessor(id, templateProcessor);
    },
    get: () => state,
    set: async (path: string, data: unknown) => {
      await templateProcessor.setData(path, data);
    },
    setStatesFromQueryResults: (states: PartialQueryResults) => {
      state.queryResultStatuses = { ...state.queryResultStatuses, ...states };
      logger.log('set states from query results: ', states);
      listeners.forEach(listener => listener());
    },
  };
};

/** ***********************************************************************************************************
 *  TemplateProvider
 ** ***********************************************************************************************************/

interface TemplateProviderProps {
  id: string;
  json: DashboardJson;
  props: any;
  children: ReactNode;
}

type TemplateProxyState = {
  subscribe: (listener: () => void) => () => void;
  initialize: () => Promise<void>;
  get: () => TemplateProcessorState;
  set: (path: string, data: unknown) => Promise<void>;
  setStatesFromQueryResults: (states: PartialQueryResults) => void;
};

export const TemplateProvider = ({ id, json, props, children }: TemplateProviderProps) => {
  const state = useRef<TemplateProxyState | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    logger.log('TemplateProvider useEffect - createTemplateProxy', {
      json,
      props,
    });
    if (state.current === null) {
      state.current = createTemplateStore(id, json, props, emits);
      state.current.initialize().then(() => {
        setIsInitialized(true);
      });
    }

    return () => {
      if (state.current) {
        removeTemplateProcessor(id);
        state.current = null;
      }
    };
  }, []);

  useEffect(() => {
    logger.log('TemplateProvider useEffect - updateProps', {
      props,
      isInitialized,
    });
    // json.spec.props is the static props in the template
    // a deep merge of the new props and the original props defined in template is passed into updateProps
    isInitialized && state?.current?.set('/props', deepMerge({}, props, json?.spec?.props));
  }, [JSON.stringify(props), state.current, isInitialized]);

  // TODO: useEffect on JSON.stringify() is expensive but it works, need to improve this
  // Seems like JSON.stringify() has less overhead than useDeepCompareEffect() for shallow nesting
  // https://javascript.plainenglish.io/a-less-known-trick-with-react-hook-dependencies-8dafaca7a150
  useEffect(() => {
    logger.log('TemplateProvider useEffect - updateChildren', {
      json,
      isInitialized,
    });
    if (
      isInitialized &&
      state.current &&
      Array.isArray(json?.spec?.children) &&
      Number(json?.spec?.children?.length) > 0
    ) {
      state.current.set('/children', json?.spec?.children);
    }
  }, [JSON.stringify(json?.spec?.children), isInitialized]);

  useEffect(() => {
    logger.log('TemplateProvider useEffect - merge template globals with current globals', {
      isInitialized,
    });
    isInitialized && mergeTemplateGlobals(json.globals);
  }, [JSON.stringify(json?.globals), isInitialized]);

  if (!isInitialized) {
    return null; // Render nothing until the state is initialized
  }

  logger.log('TemplateProvider', { json, props, children, state });

  if (!state.current) {
    return null;
  }

  if (!isInitialized) {
    return <div>Initializing ...</div>;
  }

  return <TemplateContext.Provider value={state.current}>{children}</TemplateContext.Provider>;
};
